import Label from 'components/_utility/Label';
import { getAccessoryImage } from 'helpers/accessories';
import { getImageBaseUrl } from 'helpers/request';
import { getViewportHeight, getViewportWidth, isWindows, resolve } from 'helpers/utilities';
import 'objectFitPolyfill';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDown from 'src/assets/icons/arrow-down.svg';
import ChevronLeft from 'src/assets/icons/chevron-left.svg';
import useDataLayer from 'src/hooks/useDataLayer';
import useIsHeaderPinned from 'src/hooks/useIsHeaderPinned';
import usePrevious from 'src/hooks/usePrevious';
import useSteps from 'src/hooks/useSteps';
import theme from 'src/lib/theme';
import { ctaSelector } from 'src/redux/app/selectors';
import { clearPreviewedPack, closeAccessoryResolution } from 'src/redux/bike/actions';
import {
  bikeColourSelector,
  bikeModelSelector,
  getAccessoryPendingRemoval,
  getAllBikeAccessories,
  getPendingAccessories,
  getPendingPack,
  getPreviewedPack,
} from 'src/redux/bike/selectors';
import {
  closeModal,
  flipBike,
  openModal,
  setAccessoriesCategory,
  setStep,
  zoomOff,
  zoomOn,
} from 'src/redux/configurator/actions';
import {
  accessoryCategoryIdSelector,
  currentStepSelector,
  getBikeFlipped,
  getBikeZoomed,
} from 'src/redux/configurator/selectors';
import {
  BackButton,
  BackButtonInner,
  BackLabel,
  BikeButtonContainer,
  Button,
  ButtonLabel,
  ButtonWrap,
  Icon,
  ImageWrap,
  Title,
  TitleWrap,
  Wrap,
  BikeButtonWrapper,
  SummaryToggle,
} from './BikeImage.styled';
import { Image } from './Magnify';

const BikeImage = ({ isFullScreen, className, canGoBack, innerRef, stepPaneRef, summaryHeadingRef }) => {
  const pushToDataLayer = useDataLayer();
  const dispatch = useDispatch();
  const steps = useSteps();

  const bikeModel = useSelector(bikeModelSelector);
  const bikeColour = useSelector(bikeColourSelector);
  const bikeFlipped = useSelector(getBikeFlipped);
  const accessories = useSelector(getAllBikeAccessories);
  const selectedAccessoryCategory = useSelector(accessoryCategoryIdSelector);
  const prevSelectedAccessoryCategory = usePrevious(selectedAccessoryCategory);
  const isBikeZoomed = useSelector(getBikeZoomed);
  const ctaLink = useSelector(ctaSelector);
  const pendingAccessories = useSelector(getPendingAccessories);
  const accessoryPendingRemoval = useSelector(getAccessoryPendingRemoval);
  const previewedPack = useSelector(getPreviewedPack);
  const pendingPack = useSelector(getPendingPack);

  const activeStepNumber = useSelector(currentStepSelector);
  const prevActiveStepNumber = usePrevious(activeStepNumber);
  const activeStep = steps.find(step => step.number === activeStepNumber);
  const previousStep = steps.find(step => step.number === activeStepNumber - 1);
  const lastStep = steps[steps.length - 1];

  const isBikeSummary = activeStep.number === lastStep.number;

  const flippedImage = bikeFlipped ? bikeColour.leftImage : bikeColour.rightImage;

  const zoomRef = useRef();
  const outerWrapRef = useRef(innerRef);

  const handlePrevClick = () => {
    if (pendingAccessories.length || pendingPack || accessoryPendingRemoval) {
      return dispatch(closeAccessoryResolution());
    }

    if (activeStep.packs && previewedPack) {
      return dispatch(clearPreviewedPack());
    }

    if (activeStep.accessories && selectedAccessoryCategory && !pendingAccessories.length) {
      return dispatch(setAccessoriesCategory(null));
    }

    if (activeStepNumber - 1 === 1) {
      return dispatch(openModal('unsavedConfigurationAbandon'));
    }

    pushToDataLayer(previousStep.gtmEvent);
    dispatch(setStep(activeStepNumber - 1));
  };

  useEffect(() => {
    // if ((activeStep.accessories && selectedAccessoryCategory) || activeStep.packs) {
    if (
      ((prevActiveStepNumber !== activeStepNumber && !isFullScreen) ||
        prevSelectedAccessoryCategory !== selectedAccessoryCategory) &&
      outerWrapRef &&
      stepPaneRef
    ) {
      const bikeImageEl = outerWrapRef.current;
      const stepPaneEl = stepPaneRef.current;

      if (getViewportWidth() <= theme.breakpoints.mobileEnd) {
        stepPaneEl.style.minHeight = `${getViewportHeight() - bikeImageEl.offsetHeight}px`;
        window.scrollTo(0, window.pageYOffset + bikeImageEl.getBoundingClientRect().top);
      }
    }
  }, [
    activeStep,
    activeStepNumber,
    isFullScreen,
    prevActiveStepNumber,
    prevSelectedAccessoryCategory,
    selectedAccessoryCategory,
    stepPaneRef,
  ]);

  useEffect(() => {
    window.objectFitPolyfill();
  }, [accessories]);

  const bikeIcons = [
    {
      id: !isFullScreen ? 'Expand' : 'Compress',
      label: !isFullScreen ? 'expandLabel' : 'compressLabel',
      icon: !isFullScreen
        ? '/images/configurator/bike-image/arrows-alt.svg'
        : '/images/configurator/bike-image/compress.svg',
      onClick: () =>
        !isFullScreen
          ? [dispatch(openModal('expand')), pushToDataLayer('configuratorFullScreen')]
          : dispatch(closeModal('expand')),
      testId: 'expand',
    },
    {
      id: 'Zoom',
      label: 'zoomLabel',
      icon: '/images/configurator/bike-image/search-plus.svg',
      onClick: () => dispatch(zoomOn()),
      ref: zoomRef,
      hideOnMobile: true,
      testId: 'zoom',
      props: {
        isZoomButton: true,
      },
    },
    {
      id: 'Flip',
      label: 'flipLabel',
      icon: '/images/configurator/bike-image/arrows-h.svg',
      onClick: () => [dispatch(flipBike()), pushToDataLayer('configuratorFlip')],
      testId: 'flip',
    },
  ];

  const headerHeight = `${document.querySelector('.header')?.clientHeight || 60}px`;
  const isHeaderPinned = useIsHeaderPinned();

  const scrollToSummary = () => {
    const summaryHeadingEl = summaryHeadingRef.current;
    summaryHeadingEl.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Wrap className={className} isFullScreen={isFullScreen} isBikeSummary={isBikeSummary} ref={outerWrapRef}>
      <TitleWrap
        className={isHeaderPinned ? 'is-pinned' : 'is-unpinned'}
        headerHeight={headerHeight}
        isFullScreen={isFullScreen}
        backStep={canGoBack}
        lensOn={isBikeZoomed}
      >
        <BackButton canGoBack={canGoBack} onClick={handlePrevClick} isWindows={isWindows} isFullScreen={isFullScreen}>
          <BackButtonInner>
            <ChevronLeft />
            <BackLabel>
              <Label name="backLabel" />
            </BackLabel>
          </BackButtonInner>
        </BackButton>
        <Title
          isFullScreen={isFullScreen}
          dangerouslySetInnerHTML={{ __html: bikeModel.name }}
          backStep={canGoBack}
          onClick={canGoBack ? handlePrevClick : () => {}}
          lensOn={isBikeZoomed}
        />
      </TitleWrap>

      <ImageWrap
        on={isBikeZoomed}
        onOff={() => dispatch(zoomOff())}
        triggerRef={zoomRef}
        outerWrapRef={outerWrapRef}
        isFullScreen={isFullScreen}
        isBikeSummary={isBikeSummary}
        backButton={canGoBack}
      >
        <Image
          src={getImageBaseUrl() + flippedImage}
          alt={bikeModel.name}
          isFullScreen={isFullScreen}
          data-testid="bike-image"
          data-object-fit
        />
        {accessories.length
          ? accessories.map(accessory => {
              const image = getAccessoryImage(accessories, accessory);

              if (
                !image ||
                (!bikeFlipped && !resolve(image, 'rightImage.src')) ||
                (bikeFlipped && !resolve(image, 'leftImage.src'))
              ) {
                return null;
              }

              return (
                <Image
                  src={bikeFlipped ? getImageBaseUrl() + image.leftImage.src : getImageBaseUrl() + image.rightImage.src}
                  alt={bikeFlipped ? image.leftImage.alt : image.rightImage.alt}
                  isFullScreen={isFullScreen}
                  zIndex={image.layer}
                  key={accessory.id}
                  data-object-fit
                />
              );
            })
          : null}
      </ImageWrap>

      <BikeButtonWrapper>
        <BikeButtonContainer
          isFullScreen={isFullScreen}
          isBikeSummary={isBikeSummary}
          active={isBikeZoomed}
          buttonLength={bikeIcons.filter(bikeIcon => !bikeIcon.hidden).length}
        >
          {bikeIcons.map(({ ...bikeIcon }) =>
            !bikeIcon.hidden ? (
              <ButtonWrap
                key={bikeIcon.id}
                hideOnMobile={bikeIcon.hideOnMobile}
                isFullScreen={isFullScreen}
                {...bikeIcon.props}
              >
                <>
                  <Button tabIndex="-1" ref={bikeIcon.ref} onClick={bikeIcon.onClick} data-testid={bikeIcon.testId}>
                    <Icon src={bikeIcon.icon}></Icon>
                  </Button>
                  <ButtonLabel isBikeSummary={isBikeSummary}>
                    <Label name={bikeIcon.label} />
                  </ButtonLabel>
                </>
              </ButtonWrap>
            ) : null
          )}
        </BikeButtonContainer>
      </BikeButtonWrapper>
      {activeStep.label === 'bikeSummaryLabel' ? (
        <SummaryToggle onClick={scrollToSummary}>
          <Label name="bikeSummaryLabel" />
          <ArrowDown></ArrowDown>
        </SummaryToggle>
      ) : null}
    </Wrap>
  );
};

export default BikeImage;
